export const hexToRgb = (hex: string): [number, number, number] => {
  const results = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return results ? [parseInt(results[1], 16), parseInt(results[2], 16), parseInt(results[3], 16)] : [0, 0, 0];
};

export const withOpacity = (hexColor: string, opacity: number): string =>
  `rgba(${hexToRgb(hexColor).concat(opacity).join(', ')})`;

const ink100 = '#2F2F2F';
const ink20 = '#F1F0ED';
const white100 = '#ffffff';
const black100 = '#00000';

const values = {
  ink10: '#F5F4F2',
  ink20,
  ink50: '#9B9B9B',
  ink100,
  ink110: '#1A1A1A',
  ink120: '#000000',
  primary10: '#fdf3f0',
  primary20: '#fdccb5',
  orangelight: '#f99e7a', // TODO This color remains and have no correspondance in new colors naming
  primary100: '#ea663f',
  darkprimary: '#de613c', // TODO This color remains and have no correspondance in new colors naming
  pink20: '#FFE6F2',
  pink40: '#FFCDE5',
  pink100: '#FF82BF',
  pink120: '#F261A1',
  yellow20: '#FCF5CC',
  yellow60: '#F6E066',
  yellow100: '#f0cc00',
  blue100: '#C0D3E5',
  cream100: '#f7eddb',
  error10: '#FCECED',
  error100: '#e13f48',
  success10: '#E7F6F0',
  success100: '#079c66',
  info10: '#E8F2F9',
  info100: '#1b7cc0',
  white100,
  // helpers
  background: white100,
  border: ink20,
  disabled: withOpacity(ink100, 0.7),
  a20ink100: withOpacity(ink100, 0.2),
  transparent: 'transparent',
  whiteAlpha50: withOpacity(white100, 0.5),
  whiteAlpha60: withOpacity(white100, 0.6),
  backdrop: withOpacity(black100, 0.6),
};

export type Color = keyof typeof values;

const colors: Record<Color, string> = values;

export default colors;

export const gradientValues = {
  angle: 60,
  colors: {
    default: ['#EB8030', colors.primary100, colors.primary100, colors.pink120],
    gradient50: ['#F5BE99', '#F5B39F', '#F5B39F', '#F8B0CB'],
    gradient110: ['#DF7830', '#DF623D', '#DF623D', '#E65D8E'],
    gradient120: ['#D4712D', '#D45D3A', '#D45D3A', '#DA5891'],
  },
};

export type GradientVariant = keyof typeof gradientValues.colors;

/**
 * Returns the most contrasted text color for a given background color.
 * @param hexBgColor Full hexadecimal string background color to get a text color for.
 * @example
 * - getTextColorForBackground('#ffffff') = colors.ink100.
 * - getTextColorForBackground('#000000') = colors.white100
 * @links
 * https://stackoverflow.com/a/3943023/6593810
 */
export const getTextColorForBackground = (
  hexBgColor: string,
  darkColor: string = colors.ink100,
  lightColor: string = colors.white100,
): string => {
  const [red, green, blue] = hexToRgb(hexBgColor);
  return red * 0.299 + green * 0.587 + blue * 0.114 > 186 ? darkColor : lightColor;
};

export const horizontalGradientProps = {
  start: { x: 0, y: 0 },
  end: { x: 1, y: 0 },
};
